import {
  FabricationPurchaseOrderState,
  InputIssueFabricationPurchaseOrder
} from '../../../api/dealer-api-interface-all';

/**
 * Whether a fabrication purchase order can be issued, or why not. 'not-draft' is silent (the
 * action should not be offered); the two empty-line reasons drive the matching error toast in
 * the view.
 */
export type IssueEligibility = 'ok' | 'not-draft' | 'no-items' | 'no-orderable-items';

/**
 * Pure issue guard: only a Draft PO with at least one line, of which at least one is
 * supplier-sourced, can be issued. Kept free of view/toast/api concerns so the decision is
 * unit-testable on its own.
 */
export function canIssue(
  state: FabricationPurchaseOrderState,
  itemCount: number,
  orderableItemCount: number
): IssueEligibility {
  if (state !== FabricationPurchaseOrderState.Draft) return 'not-draft';
  if (itemCount === 0) return 'no-items';
  // Why a second count? Because only supplier-sourced lines are ordered, so a PO of nothing but
  // externally-sourced lines would send an empty quote that V6 rejects - after the PO had already
  // moved to IssuedPending.
  if (orderableItemCount === 0) return 'no-orderable-items';
  return 'ok';
}

/**
 * Everything the dispatch needs, injected so the post -> reload sequencing is unit-testable without
 * a live view, api factory, or localization bootstrap. The confirmation is done by the caller,
 * before it enters saveWithIndicator, so it is not part of this contract.
 */
export interface IssueDispatchDeps {
  purchaseOrderId: string;
  /** The view's record version, so a PO changed by another user since it loaded is rejected. */
  recordVersion: string;
  /** POST the issue request; resolves a truthy result on success, null/undefined on failure. */
  issue: (input: InputIssueFabricationPurchaseOrder) => Promise<unknown>;
  /** Reload the header so the now-IssuedPending state is reflected without waiting for the SSE. */
  reload: () => Promise<void>;
  /** Show the issuing-in-progress success toast. */
  onIssued: () => void;
}

/**
 * Dispatch the issue of an already-eligible, already-confirmed fabrication purchase order. Call only
 * after canIssue(...) === 'ok' and the user has confirmed.
 *
 * @returns true when the PO was dispatched for issue; false for any failure path.
 */
export async function dispatchIssue(deps: IssueDispatchDeps): Promise<boolean> {
  let result: unknown = null;
  try {
    result = await deps.issue({
      purchaseOrderId: deps.purchaseOrderId,
      recordVersion: deps.recordVersion
    });
  } finally {
    // Why reload even when the issue was rejected or threw? Because a rejected issue can still have
    // moved the order: a failed queue dispatch commits the quote common id before reverting to Draft,
    // and that locks the line set. A raw network error rethrows past the api layer, so a finally is
    // the only way to guarantee the reload runs (and not leak an unhandled rejection unreloaded).
    // Without this the page keeps offering edits the server now refuses. On success the reload shows
    // IssuedPending immediately rather than waiting on the SSE that the same transaction pushes.
    await deps.reload();
  }
  if (!result) return false;

  deps.onIssued();
  return true;
}
